import { useLatest } from 'ahooks';
import { useRef } from 'react';
import { SliderInput } from '@meitu/saas-ui';
import { SimpleTitle } from '@meitu/saas-ui/es/title';
import { Editor } from '@meitu/saas-editor-core';
import BaseToolbar from '@meitu/saas-editor-ui/es/components/base-toolbar/view/base-toolbar';
import { DividerLine } from '@meitu/saas-editor-ui/es/components/divider-line';
import HeaderToolbar from '@meitu/saas-editor-ui/es/components/header-toolbar/view/header-toolbar';
import LabelAction from '@meitu/saas-editor-ui/es/components/label-action';
import { useListenActivePickLayerProps } from '@meitu/saas-editor-ui/es/hooks/use-listen-layer-props';
import ActiveChartLayerPanel, {
    ActiveChartPanelRef,
} from '@meitu/saas-editor-ui/es/panels/chart-layer-panel/chart-layer-panel';
import { ActionInfo } from '@meitu/saas-editor-ui/es/panels/chart-layer-panel/types';
import { checkFontIdIsTeamFontId } from '@meitu/xdesign-widgets';
import { UserBizSearchProps } from '@meitu/xdesign-widgets/dist/textFontFamily';
import { usePanelLayerId } from '@/hooks';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { toWidgetFormat } from '@/services/font';
import { useStoreState } from '@/store';
import { Entrance } from '@/types';
import { openVipModal } from '@/utils/login';
import { getLayerType, HandleHbLayersFunc } from '@/utils/stat-track';
import './chart-panel.less';

const trackFirstId = 11;

const OpacityFeature: React.FC<{
    editor: Editor;
    id: string;
}> = ({ editor, id }) => {
    const { $t } = useLocaleStore();
    const originOpacity = useRef<number | null>(null);

    const opacity = useListenActivePickLayerProps(
        editor,
        (layer) => {
            if (!layer) return null;
            return layer.opacity;
        },
        true,
    );

    if (opacity === null) return null;

    return (
        <LabelAction title={$t((v) => v.panel.tablePanel.opacity)} align>
            <SliderInput
                value={+(opacity * 100).toFixed(0)}
                max={100}
                min={0}
                showStepBtn
                type="horizontal"
                onChange={(val: number) => {
                    val = val / 100;
                    if (originOpacity.current === null) {
                        originOpacity.current = opacity;
                    }
                    editor.layer.setOpacity(id, val);
                }}
                onAfterChange={(val: number) => {
                    val = val / 100;
                    editor.layer.setOpacity(id, val, originOpacity.current);
                    originOpacity.current = null;
                }}
            />
        </LabelAction>
    );
};

const ChartPanel = () => {
    const { $t } = useLocaleStore();

    const activeChartPanelRef = useRef<ActiveChartPanelRef>(null);
    const currentRecordId = useStoreState((state) => state.recordModel.recordInfo.id);
    const isVipUser = useStoreState((state) => state.userInfoModel.userInfo?.is_vip ?? false);
    const isSearchRef = useLatest(useStoreState((state) => state.searchModel.is_search));
    const fontList = toWidgetFormat(useStoreState((state) => state.fontMaterialModel.fonts));

    const layerId = usePanelLayerId()!;
    const stageType = editor.stage.getType();

    const textTrack = ({
        page_type,
        ...restProps
    }: {
        secondId: number;
        isUse?: boolean;
        isClick?: boolean;
        page_type: 'graph_data_edit' | 'edit';
    }) => {
        const activePickLayer = editor.active.get();
    };

    const onAction = (actionInfo: ActionInfo) => {
        if ((actionInfo as any).target === 'onShowBrandColorsTab') {
            window._stat_track?.('hb_layers_func_click', {
                func_id: '7_107',
            });
            return;
        }

        let doClickTrack = true;
        let secondId: number | null = null;
        let textEditTrackData: Record<any, any> | null = null;
        let page_type: 'graph_data_edit' | 'edit' = 'edit';
        switch (actionInfo.target) {
            case 'borderRaius':
                actionInfo.data === 'isUse' && (secondId = 162);
                break;
            case 'showSeriesLabel':
                secondId = 176;
                break;
            case 'changeAxisLabel':
                secondId = 177;
                break;
            case 'showLegend':
                secondId = 178;
                break;
            case 'showGrid':
                secondId = 179;
                break;
            case 'openEditModal':
                secondId = 180;
                break;
            case 'clearData':
                secondId = 181;
                page_type = 'graph_data_edit';
                break;
            case 'chartType':
                actionInfo.data.actionType === 'isUse' && (secondId = 182);
                actionInfo.data.isFromEditModal && (page_type = 'graph_data_edit');
                break;
            case 'chartColorsPreset':
                actionInfo.data === 'isUse' && (secondId = 183);
                break;
            case 'chartSerieColorChange':
                secondId = 184;
                actionInfo.data.isFromEditModal && (page_type = 'graph_data_edit');
                break;
            case 'textFontFamily':
                textEditTrackData = {
                    样式字体: actionInfo.data.id,
                    is_search: isSearchRef.current,
                };
                textTrack({
                    secondId: checkFontIdIsTeamFontId(actionInfo.data.id ?? 0) ? 107 : 7,
                    isUse: true,
                    page_type,
                });
                break;
            case 'textFontFamilyDropdownVisible':
                textTrack({
                    secondId: 7,
                    isClick: true,
                    page_type,
                });
                break;
            case 'fontSize':
                textEditTrackData = {
                    样式字号: actionInfo.data,
                    is_search: isSearchRef.current,
                };
                textTrack({
                    secondId: 8,
                    isUse: true,
                    page_type,
                });
                break;
            case 'fontSizeClick':
                textTrack({
                    secondId: 8,
                    isClick: true,
                    page_type,
                });
                break;
            case 'textColor':
                {
                    const isBrandColor = actionInfo.data.isBrandColor;
                    if (isBrandColor) {
                        doClickTrack = false;
                    }
                    doClickTrack = false;
                    textEditTrackData = {
                        样式字号: actionInfo.data.value,
                        is_search: isSearchRef.current,
                    };
                    textTrack({
                        secondId: isBrandColor ? 107 : 2,
                        isUse: true,
                        isClick: !isBrandColor,
                        page_type,
                    });
                }
                break;
            case 'pasteData':
                secondId = 185;
                page_type = 'graph_data_edit';
                break;
            case 'updateRowOrColCount':
                secondId = 57;
                page_type = 'graph_data_edit';
                break;
        }
        if (secondId !== null) {
            const activePickLayer = editor.active.get();
        }
        if (textEditTrackData !== null) {
            window._stat_track('hb_text_edit_yes_data', textEditTrackData);
        }
    };

    return (
        <ActiveChartLayerPanel
            ref={activeChartPanelRef}
            editor={editor}
            layerId={layerId}
            fontList={fontList}
            isVipUser={isVipUser}
            handleShowVip={() =>
                openVipModal({
                    entrance: Entrance.Material,
                })
            }
            onAction={onAction}
            textFontFamilyComponentUserBizSearchProps={UserBizSearchProps}
        >
            {({ ChartPanel, TextPanel }) => (
                <>
                    <HeaderToolbar id={layerId} editor={editor} style={{ padding: 16 }} />

                    <div style={{ padding: '0 16px' }}>
                        {ChartPanel}

                        <div style={{ height: 5 }}></div>

                        {/* 图形透明度 */}
                        <OpacityFeature editor={editor} id={layerId} />
                    </div>

                    <DividerLine />

                    <div style={{ padding: '0 16px' }}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <SimpleTitle text={$t((v) => v.panel.tablePanel.textStyle)} fontSize={14} />
                        </div>
                        <div style={{ marginTop: 8 }}>{TextPanel}</div>
                    </div>

                    <DividerLine />

                    {/* 通用功能 */}
                    <BaseToolbar id={layerId} editor={editor} style={{ padding: '0 16px 16px' }} />
                </>
            )}
        </ActiveChartLayerPanel>
    );
};

ChartPanel.displayName = 'ChartPanel';

export default ChartPanel;
